// Matches a media filename to the product(s) it names. Shoot files for co-ord sets often name
// every piece — "…-city-glow-top-black-city-glow-shorts-black_03.jpg" — and that shot belongs
// on each of those products, so this returns every product named, not just the best one.

export const toSlug = s => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const MIN_SLUG = 6;   // shorter slugs ("top", "black") would match half the collection

function slugsOf(p) {
  return [p.handle, p.title, p.titleNoColour].filter(Boolean).map(toSlug).filter(s => s.length >= MIN_SLUG);
}

// The product's colour as it might appear in a filename: its colour field, or what its title
// adds on top of the no-colour title ("City Glow Top Black" − "City Glow Top" → "black").
function colourSlugsOf(p) {
  const out = [toSlug(p.metaColor)];
  const t = toSlug(p.title), base = toSlug(p.titleNoColour);
  if (t && base && t.startsWith(base) && t.length > base.length) out.push(t.slice(base.length).replace(/^-+/, ''));
  return out.filter(c => c.length >= 3);
}

/**
 * Products named in `filename`, in the order they appear in it. Each product counts once, at
 * its longest matching slug. A match lying inside a longer match for another product loses
 * to it ("city-glow-shorts" inside "city-glow-shorts-black"). Products tied on the very same
 * text — colourways sharing a no-colour title — resolve to the one whose own colour/title
 * also appears in the name, else the first, so a shot never spreads across every colourway.
 */
export function matchProductsInFilename(filename, products) {
  const fileSlug = toSlug(String(filename || '').replace(/\.[^.]+$/, ''));
  if (!fileSlug) return [];

  const hits = [];
  for (const p of products) {
    let best = null;
    for (const slug of slugsOf(p)) {
      const at = fileSlug.indexOf(slug);
      if (at !== -1 && (!best || slug.length > best.len)) best = { start: at, end: at + slug.length, len: slug.length };
    }
    if (best) hits.push({ p, ...best });
  }

  // Drop hits strictly inside a longer hit for a different product.
  const outer = hits.filter(h => !hits.some(o => o !== h && o.len > h.len && o.start <= h.start && o.end >= h.end));

  // Resolve identical spans down to one product.
  const bySpan = new Map();
  for (const h of outer) {
    const key = `${h.start}:${h.end}`;
    bySpan.set(key, [...(bySpan.get(key) || []), h]);
  }
  const chosen = [];
  for (const group of bySpan.values()) {
    if (group.length === 1) { chosen.push(group[0]); continue; }
    const named = group.find(h => {
      const matched = fileSlug.slice(h.start, h.end);
      return colourSlugsOf(h.p).some(c => c !== matched && fileSlug.replace(matched, '-').includes(c));
    });
    chosen.push(named || group[0]);
  }

  return chosen.sort((a, b) => a.start - b.start).map(h => h.p);
}
